import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import { getAuthUser } from '../utils/auth';

const statusMap = {
  OPEN: { label: 'Abierto', color: 'bg-blue-100 text-blue-700' },
  CLOSED: { label: 'Cerrado', color: 'bg-green-100 text-green-700' },
  ABSENCE_VACATION: { label: 'Vacaciones', color: 'bg-yellow-100 text-yellow-700' },
  ABSENCE_SICK: { label: 'Enfermedad', color: 'bg-red-100 text-red-700' },
  ABSENCE_UNPAID: { label: 'Sin sueldo', color: 'bg-orange-100 text-orange-700' },
  ABSENCE_PERSONAL: { label: 'Personal', color: 'bg-indigo-100 text-indigo-700' },
  ABSENCE_OTHER: { label: 'Otra', color: 'bg-gray-100 text-gray-700' },
  NO_RECORD: { label: 'Sin registro', color: 'bg-gray-100 text-gray-700' },
};

const formatTime = (value) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const formatMinutesToHours = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '00:00';
  }
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const formatLocation = (latitude, longitude) => {
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
    return null;
  }
  const latValue = Number(latitude);
  const lonValue = Number(longitude);
  if (Number.isNaN(latValue) || Number.isNaN(lonValue)) {
    return null;
  }
  return `${latValue.toFixed(5)}, ${lonValue.toFixed(5)}`;
};

const getRecordLocation = (record, type) => {
  if (!record) {
    return null;
  }
  if (Array.isArray(record.punches) && record.punches.length > 0) {
    const punches = record.punches
      .filter((punch) => punch.type === type)
      .sort((a, b) => new Date(a.punchedAt).getTime() - new Date(b.punchedAt).getTime());
    if (punches.length > 0) {
      const punch = type === 'IN' ? punches[0] : punches[punches.length - 1];
      const punchLocation = formatLocation(punch?.latitude, punch?.longitude);
      if (punchLocation) {
        return punchLocation;
      }
    }
  }
  if (type === 'IN') {
    return formatLocation(record.checkInLatitude, record.checkInLongitude);
  }
  return formatLocation(record.checkOutLatitude, record.checkOutLongitude);
};

const getCurrentLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ location: null, error: 'Geolocalizacion no disponible.' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          },
          error: null,
        }),
      () => resolve({ location: null, error: 'No se pudo obtener la ubicacion.' }),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
    );
  });

export default function AttendanceToday() {
  const today = new Date().toISOString().slice(0, 10);
  const authUser = getAuthUser();
  const isEmployee = authUser?.role === 'EMPLOYEE';
  const isAdmin = ['ADMIN', 'HR'].includes(authUser?.role);
  const isHr = authUser?.role === 'HR';
  const currentEmployeeId = authUser?.employeeId ?? null;
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [flexSchedules, setFlexSchedules] = useState([]);
  const [selectedFlexScheduleId, setSelectedFlexScheduleId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [locationWarning, setLocationWarning] = useState(null);
  const reportFrom = isAdmin ? dateFrom : today;
  const reportTo = isAdmin ? dateTo : today;
  const isSingleDate = reportFrom === reportTo;

  const hrDepartmentId = useMemo(() => {
    if (!isHr || !currentEmployeeId) {
      return null;
    }
    const hrEmployee = employees.find((employee) => employee.id === currentEmployeeId);
    return hrEmployee?.departmentId ?? null;
  }, [employees, isHr, currentEmployeeId]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    if (isAdmin && reportFrom && reportTo && reportFrom > reportTo) {
      setError('La fecha "desde" no puede ser posterior a "hasta".');
      setRecords([]);
      setIsLoading(false);
      return;
    }
    try {
      if (isEmployee && !currentEmployeeId) {
        setError('Usuario sin empleado asociado.');
        setEmployees([]);
        setRecords([]);
      } else if (isEmployee && currentEmployeeId) {
        const [employeeResponse, recordsResponse] = await Promise.all([
          axios.get(`/employees/${currentEmployeeId}`),
          axios.get(`/attendance/employee/${currentEmployeeId}`, {
            params: { from: reportFrom, to: reportTo },
          }),
        ]);
        const employeeData = employeeResponse.data;
        setEmployees([employeeData]);
        const normalized = recordsResponse.data.map((record) => ({
          ...record,
          id: record.recordId ?? record.id,
          employeeId: record.employeeId ?? currentEmployeeId,
        }));
        setRecords(normalized);
        if (employeeData.workScheduleType === 'FLEX') {
          const flexResponse = await axios.get('/schedules/flex');
          setFlexSchedules(flexResponse.data);
          setSelectedFlexScheduleId((prev) => {
            if (prev) {
              return prev;
            }
            if (employeeData.workScheduleId) {
              return String(employeeData.workScheduleId);
            }
            return flexResponse.data[0]?.id ? String(flexResponse.data[0].id) : '';
          });
        } else {
          setFlexSchedules([]);
          setSelectedFlexScheduleId('');
        }
      } else {
        const requests = [
          axios.get('/employees'),
          axios.get('/attendance', { params: { from: reportFrom, to: reportTo } }),
        ];
        if (isAdmin) {
          requests.push(axios.get('/departments'));
        }
        const [employeesResponse, recordsResponse, departmentsResponse] = await Promise.all(requests);
        setEmployees(employeesResponse.data);
        setRecords(recordsResponse.data);
        if (isAdmin && departmentsResponse) {
          setDepartments(departmentsResponse.data);
        }
      }
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudieron cargar los datos de asistencia.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [today, isEmployee, currentEmployeeId, isAdmin, reportFrom, reportTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (isHr && hrDepartmentId) {
      setDepartmentFilter(String(hrDepartmentId));
    }
  }, [isHr, hrDepartmentId]);

  const rows = useMemo(() => {
    let baseRows = [];
    if (isSingleDate) {
      baseRows = employees.map((employee) => {
        const record = records.find(
          (item) => item.employeeId === employee.id && item.date === reportFrom,
        );
        const statusKey = record ? record.status : 'NO_RECORD';
        return {
          rowKey: `employee-${employee.id}`,
          employeeId: employee.id,
          recordId: record?.id ?? null,
          date: reportFrom,
          employee: `${employee.firstName} ${employee.lastName}`,
          department: employee.departmentName || employee.departmentId || '--',
          departmentId: employee.departmentId ?? null,
          statusKey,
          checkInAt: record?.checkInAt ?? null,
          checkOutAt: record?.checkOutAt ?? null,
          lateMinutes: record?.lateMinutes ?? 0,
          workedMinutes: record?.workedMinutes ?? 0,
          checkInLocation: getRecordLocation(record, 'IN'),
          checkOutLocation: getRecordLocation(record, 'OUT'),
        };
      });
    } else {
      const employeeLookup = new Map(employees.map((employee) => [employee.id, employee]));
      baseRows = records.map((record) => {
        const employee = employeeLookup.get(record.employeeId);
        return {
          rowKey: `record-${record.id}`,
          employeeId: record.employeeId,
          recordId: record.id ?? null,
          date: record.date,
          employee: employee
            ? `${employee.firstName} ${employee.lastName}`
            : `Empleado ${record.employeeId}`,
          department: employee?.departmentName || employee?.departmentId || '--',
          departmentId: employee?.departmentId ?? null,
          statusKey: record.status ?? 'NO_RECORD',
          checkInAt: record.checkInAt ?? null,
          checkOutAt: record.checkOutAt ?? null,
          lateMinutes: record.lateMinutes ?? 0,
          workedMinutes: record.workedMinutes ?? 0,
          checkInLocation: getRecordLocation(record, 'IN'),
          checkOutLocation: getRecordLocation(record, 'OUT'),
        };
      });
    }
    let filteredRows = baseRows;
    if (isAdmin && departmentFilter) {
      filteredRows = filteredRows.filter(
        (row) => row.departmentId !== null && String(row.departmentId) === departmentFilter,
      );
    }
    if (isAdmin && employeeFilter) {
      filteredRows = filteredRows.filter((row) => String(row.employeeId) === employeeFilter);
    }
    return filteredRows;
  }, [employees, records, reportFrom, isAdmin, departmentFilter, employeeFilter, isSingleDate]);

  const isFlexEmployee = isEmployee && employees[0]?.workScheduleType === 'FLEX';
  const isActionDate = isSingleDate && reportFrom === today;
  const visibleDepartments =
    isHr && hrDepartmentId
      ? departments.filter((department) => department.id === hrDepartmentId)
      : departments;

  const handleCheckIn = async (employeeId) => {
    if (isFlexEmployee && !selectedFlexScheduleId) {
      setError('Selecciona un horario flex antes de registrar la entrada.');
      return;
    }
    setActionLoadingId(employeeId);
    setError(null);
    setLocationWarning(null);
    try {
      const { location, error: locationError } = await getCurrentLocation();
      if (!location) {
        setLocationWarning(locationError || 'Ubicacion no disponible para este registro.');
      }
      const payload = {
        employeeId,
        checkInAt: new Date().toISOString(),
        date: reportFrom,
        latitude: location?.latitude,
        longitude: location?.longitude,
      };
      if (isFlexEmployee && selectedFlexScheduleId) {
        payload.workScheduleId = Number(selectedFlexScheduleId);
      }
      await axios.post('/attendance/check-in', payload);
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo registrar la entrada.';
      setError(message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCheckOut = async (recordId, employeeId) => {
    if (!recordId) {
      setError('No hay registro abierto para este empleado.');
      return;
    }
    setActionLoadingId(employeeId);
    setError(null);
    setLocationWarning(null);
    try {
      const { location, error: locationError } = await getCurrentLocation();
      if (!location) {
        setLocationWarning(locationError || 'Ubicacion no disponible para este registro.');
      }
      await axios.patch(`/attendance/${recordId}/check-out`, {
        checkOutAt: new Date().toISOString(),
        latitude: location?.latitude,
        longitude: location?.longitude,
      });
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo registrar la salida.';
      setError(message);
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Asistencia del dia</h1>
        <p className="text-sm text-gray-500">
          {isSingleDate
            ? `Resumen operativo de ${reportFrom}`
            : `Resumen operativo de ${reportFrom} a ${reportTo}`}
        </p>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}
      {locationWarning && (
        <div className="mb-4 rounded-lg bg-yellow-50 px-4 py-2 text-sm text-yellow-700">
          {locationWarning}
        </div>
      )}

      {isFlexEmployee && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-gray-700" htmlFor="flexSchedule">
            Horario flex
          </label>
          <select
            id="flexSchedule"
            value={selectedFlexScheduleId}
            onChange={(event) => setSelectedFlexScheduleId(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
          >
            <option value="">Selecciona un horario</option>
            {flexSchedules.map((schedule) => (
              <option key={schedule.id} value={String(schedule.id)}>
                {schedule.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {isAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-semibold text-gray-700" htmlFor="dateFrom">
              Desde
            </label>
            <input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-semibold text-gray-700" htmlFor="dateTo">
              Hasta
            </label>
            <input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-semibold text-gray-700" htmlFor="employeeFilter">
              Filtrar por empleado
            </label>
            <select
              id="employeeFilter"
              value={employeeFilter}
              onChange={(event) => setEmployeeFilter(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Todos</option>
              {employees.map((employee) => (
                <option key={employee.id} value={String(employee.id)}>
                  {employee.firstName} {employee.lastName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-semibold text-gray-700" htmlFor="departmentFilter">
              Filtrar por departamento
            </label>
            <select
              id="departmentFilter"
              value={departmentFilter}
              onChange={(event) => setDepartmentFilter(event.target.value)}
              disabled={isHr}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Todos</option>
              {visibleDepartments.map((department) => (
                <option key={department.id} value={String(department.id)}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Cargando asistencia...</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Colaborador
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Area
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Entrada
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Ubicacion
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Salida
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Ubicacion salida
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Retraso
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Horas trabajadas
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Operacion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row) => {
                const status = statusMap[row.statusKey] ?? statusMap.NO_RECORD;
                const isActionLoading = actionLoadingId === row.employeeId;
                const canCheckIn = row.statusKey === 'NO_RECORD' && isActionDate;
                const canCheckOut = row.statusKey === 'OPEN' && row.recordId && isActionDate;
                return (
                  <tr key={row.rowKey} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.employee}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{row.department}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{row.date}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatTime(row.checkInAt)}</td>
                    <td
                      className={`px-6 py-4 text-sm ${
                        row.checkInLocation ? 'text-gray-500' : 'text-red-600 font-semibold'
                      }`}
                    >
                      {row.checkInLocation ?? 'Sin ubicacion'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatTime(row.checkOutAt)}</td>
                    <td
                      className={`px-6 py-4 text-sm ${
                        row.checkOutLocation ? 'text-gray-500' : 'text-red-600 font-semibold'
                      }`}
                    >
                      {row.checkOutLocation ?? 'Sin ubicacion'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {row.lateMinutes > 0 ? `${row.lateMinutes} min` : '--'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatMinutesToHours(row.workedMinutes)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleCheckIn(row.employeeId)}
                          disabled={!canCheckIn || isActionLoading}
                          className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          Entrada
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCheckOut(row.recordId, row.employeeId)}
                          disabled={!canCheckOut || isActionLoading}
                          className="rounded bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          Salida
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
